import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  TablePagination,
  IconButton,
  Checkbox,
  Button,
  TextField,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
  Switch,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import RestoreIcon from '@mui/icons-material/Restore';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import './AutomatedReminders.css';

// Format day + time as "Every month 5th at 6pm"
function formatReminderExecution(day, time) {
  if (day == null && time == null) return '—';
  const dayPart = day != null ? `${day}${getOrdinal(day)}` : '—';
  const timePart = time != null ? formatHour(time) : '';
  if (!timePart) return `Every month ${dayPart}`;
  return `Every month ${dayPart} at ${timePart}`;
}

function getOrdinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

function formatHour(hour24) {
  if (hour24 === 0) return '12am';
  if (hour24 === 12) return '12pm';
  if (hour24 < 12) return `${hour24}am`;
  return `${hour24 - 12}pm`;
}

function formatDateTime(isoString) {
  if (!isoString) return '—';
  const d = new Date(isoString);
  return d.toLocaleString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

const TABLE_HEADER_SX = {
  backgroundColor: '#1e40af',
  color: '#ffffff',
  fontWeight: 600,
  fontSize: '13px',
};

function getReminderComparator(orderBy, order) {
  return (a, b) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];
    if (orderBy === 'updated_at') {
      aVal = new Date(aVal || 0).getTime();
      bVal = new Date(bVal || 0).getTime();
      const cmp = aVal - bVal;
      return order === 'asc' ? cmp : -cmp;
    }
    if (orderBy === 'default_day' || orderBy === 'updated_day') {
      const timeKey = orderBy === 'default_day' ? 'default_time' : 'updated_time';
      aVal = aVal != null ? Number(aVal) : -1;
      bVal = bVal != null ? Number(bVal) : -1;
      const dayCmp = aVal - bVal;
      if (dayCmp !== 0) return order === 'asc' ? dayCmp : -dayCmp;
      const aTime = a[timeKey] != null ? Number(a[timeKey]) : -1;
      const bTime = b[timeKey] != null ? Number(b[timeKey]) : -1;
      const timeCmp = aTime - bTime;
      return order === 'asc' ? timeCmp : -timeCmp;
    }
    const aStr = String(aVal ?? '').toLowerCase();
    const bStr = String(bVal ?? '').toLowerCase();
    const cmp = aStr.localeCompare(bStr, undefined, { numeric: true });
    return order === 'asc' ? cmp : -cmp;
  };
}

const MONTHS = [
  { value: '', label: 'Every month' },
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const getTimeLabel = (hour24) => {
  if (hour24 === 0) return '12:00 AM';
  if (hour24 === 12) return '12:00 PM';
  if (hour24 < 12) return `${hour24}:00 AM`;
  return `${hour24 - 12}:00 PM`;
};

function ReminderSubTab({ email = '', setAlert, setWarning }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('default_day');
  const [order, setOrder] = useState('asc');
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editDay, setEditDay] = useState(1);
  const [editTime, setEditTime] = useState(0);
  const [editSaving, setEditSaving] = useState(false);
  const [resetRow, setResetRow] = useState(null);
  const [resetSaving, setResetSaving] = useState(false);

  const fetchReminders = async () => {
    try {
      const api = process.env.REACT_APP_GET_AUTOMATED_REMINDERS_LIST;
      if (!api) {
        setError('API not configured');
        setLoading(false);
        return;
      }
      const res = await axios.get(api);
      const data = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load reminders');
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReminders();
  }, []);

  const handleEdit = (row) => {
    setEditRow(row);
    setEditDay(row.updated_day ?? row.default_day ?? 1);
    setEditTime(row.updated_time ?? row.default_time ?? 0);
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setEditRow(null);
    setEditDay(1);
    setEditTime(0);
  };

  const isSameAsDefault =
    editRow &&
    editRow.default_day != null &&
    editRow.default_time != null &&
    Number(editDay) === Number(editRow.default_day) &&
    Number(editTime) === Number(editRow.default_time);

  const isRowCustom = (row) => {
    if (!row || row.default_day == null || row.default_time == null) return false;
    if (row.updated_day == null && row.updated_time == null) return false;
    return (
      Number(row.updated_day ?? row.default_day) !== Number(row.default_day) ||
      Number(row.updated_time ?? row.default_time) !== Number(row.default_time)
    );
  };

  const handleResetConfirm = async () => {
    if (!resetRow) return;
    const api = process.env.REACT_APP_POST_UPDATE_AUTOMATED_REMINDER;
    if (!api) {
      setWarning?.('API not configured (REACT_APP_POST_UPDATE_AUTOMATED_REMINDER)');
      setResetRow(null);
      return;
    }
    setResetSaving(true);
    try {
      const payload = {
        reminder_name: resetRow.reminder_name ?? '',
        updated_day: null,
        updated_time: null,
        updated_by: email || '',
        reset_to_default: true,
      };
      const res = await axios.post(api, payload);
      if (res?.status >= 200 && res?.status < 300) {
        setAlert?.('Reminder reset to default settings.');
        setResetRow(null);
        await fetchReminders();
      } else {
        setWarning?.(res?.data?.message || 'Failed to reset reminder.');
      }
    } catch (err) {
      setWarning?.(err?.response?.data?.message || err?.message || 'Failed to reset reminder.');
    } finally {
      setResetSaving(false);
    }
  };

  const handleEditSave = async () => {
    if (!editRow) return;
    if (isSameAsDefault) {
      setWarning?.('Day and time must be different from default to update.');
      return;
    }
    const api = process.env.REACT_APP_POST_UPDATE_AUTOMATED_REMINDER;
    if (!api) {
      setWarning?.('API not configured (REACT_APP_POST_UPDATE_AUTOMATED_REMINDER)');
      return;
    }
    setEditSaving(true);
    try {
      const payload = {
        reminder_name: editRow.reminder_name ?? '',
        updated_day: Number(editDay),
        updated_time: Number(editTime),
        updated_by: email || '',
      };
      const res = await axios.post(api, payload);
      if (res?.status >= 200 && res?.status < 300) {
        setAlert?.('Reminder updated successfully.');
        await fetchReminders();
        handleEditClose();
      } else {
        setWarning?.(res?.data?.message || 'Failed to update reminder.');
      }
    } catch (err) {
      setWarning?.(err?.response?.data?.message || err?.message || 'Failed to update reminder.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleSort = (columnId) => () => {
    const isAsc = orderBy === columnId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnId);
    setPage(0);
  };

  const sortedList = [...list].sort(getReminderComparator(orderBy, order));
  const paginatedList = sortedList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) {
    return (
      <div className="automated-reminders-loading">
        <CircularProgress size={36} />
      </div>
    );
  }

  if (error) {
    return <div className="automated-reminders-error">{error}</div>;
  }

  return (
    <div className="automated-reminders-tab-content">
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
          borderRadius: '8px',
          maxHeight: 'calc(100vh - 340px)',
          overflow: 'auto',
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...TABLE_HEADER_SX }}>
                <TableSortLabel
                  active={orderBy === 'reminder_name'}
                  direction={orderBy === 'reminder_name' ? order : undefined}
                  onClick={handleSort('reminder_name')}
                  sx={{ color: '#fff' }}
                >
                  Reminder Title
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'default_day'}
                  direction={orderBy === 'default_day' ? order : undefined}
                  onClick={handleSort('default_day')}
                  sx={{ color: '#fff' }}
                >
                  Default Reminder Execution
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'updated_day'}
                  direction={orderBy === 'updated_day' ? order : undefined}
                  onClick={handleSort('updated_day')}
                  sx={{ color: '#fff' }}
                >
                  Updated Reminder Execution
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'updated_at'}
                  direction={orderBy === 'updated_at' ? order : undefined}
                  onClick={handleSort('updated_at')}
                  sx={{ color: '#fff' }}
                >
                  Last Updated At
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'updated_by'}
                  direction={orderBy === 'updated_by' ? order : undefined}
                  onClick={handleSort('updated_by')}
                  sx={{ color: '#fff' }}
                >
                  Last Updated By
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center', width: 80 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedList.length > 0 ? (
              paginatedList.map((row, idx) => (
                <TableRow
                  key={`${page * rowsPerPage + idx}-${row.reminder_name ?? idx}`}
                  sx={{
                    '&:hover': { backgroundColor: '#e8f4fd' },
                    backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8faff',
                  }}
                >
                  <TableCell sx={{ fontSize: '12px' }}>{row.reminder_name ?? '—'}</TableCell>
                  <TableCell sx={{ fontSize: '12px', textAlign: 'center' }}>
                    {formatReminderExecution(row.default_day, row.default_time)}
                  </TableCell>
                  <TableCell sx={{ fontSize: '12px', textAlign: 'center' }}>
                    {formatReminderExecution(row.updated_day, row.updated_time)}
                  </TableCell>
                  <TableCell sx={{ fontSize: '12px', textAlign: 'center' }}>{formatDateTime(row.updated_at)}</TableCell>
                  <TableCell sx={{ fontSize: '12px', textAlign: 'center' }}>{row.updated_by ?? '—'}</TableCell>
                  <TableCell sx={{ fontSize: '12px', textAlign: 'center', padding: '4px' }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(row)}
                      aria-label="Edit"
                      sx={{
                        color: '#1e40af',
                        '&:hover': { backgroundColor: '#e8f4fd' },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {isRowCustom(row) && (
                      <Tooltip title="Reset to default settings">
                        <IconButton
                          size="small"
                          onClick={() => setResetRow(row)}
                          aria-label="Reset to default"
                          sx={{
                            color: '#ea580c',
                            ml: 0.5,
                            '&:hover': { backgroundColor: '#fff7ed' },
                          }}
                        >
                          <RestoreIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
                  <Typography variant="body2">No reminders found.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 15, 20, 50]}
          component="div"
          count={list.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ borderTop: '1px solid #e2e8f0' }}
        />
      </TableContainer>

      <Dialog open={editOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Reminder Execution</DialogTitle>
        <DialogContent>
          {editRow && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="Reminder Title"
                value={editRow.reminder_name ?? ''}
                fullWidth
                size="small"
                disabled
                InputProps={{ readOnly: true }}
              />
              <FormControl fullWidth size="small">
                <InputLabel>Day of month</InputLabel>
                <Select
                  value={editDay}
                  label="Day of month"
                  onChange={(e) => setEditDay(Number(e.target.value))}
                >
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <MenuItem key={d} value={d}>
                      {d}{d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Time</InputLabel>
                <Select
                  value={editTime}
                  label="Time"
                  onChange={(e) => setEditTime(Number(e.target.value))}
                >
                  {Array.from({ length: 24 }, (_, i) => i).map((h) => (
                    <MenuItem key={h} value={h}>
                      {getTimeLabel(h)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {isSameAsDefault && (
                <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                  Change day or time to something different from default to update.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            disabled={editSaving || isSameAsDefault}
            sx={{ backgroundColor: '#1e40af', '&:hover': { backgroundColor: '#1d4ed8' } }}
          >
            {editSaving ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(resetRow)} onClose={() => !resetSaving && setResetRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset to Default Settings</DialogTitle>
        <DialogContent>
          {resetRow && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
              <DialogContentText sx={{ fontSize: '14px' }}>
                Reset <strong>{resetRow.reminder_name ?? '—'}</strong> back to the default schedule?
              </DialogContentText>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 1,
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  padding: '10px 12px',
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Current
                  </Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>
                    {formatReminderExecution(resetRow.updated_day, resetRow.updated_time)}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Default
                  </Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#0059B2' }}>
                    {formatReminderExecution(resetRow.default_day, resetRow.default_time)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetRow(null)} color="inherit" disabled={resetSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleResetConfirm}
            variant="contained"
            disabled={resetSaving}
            sx={{ backgroundColor: '#ea580c', '&:hover': { backgroundColor: '#c2410c' } }}
          >
            {resetSaving ? 'Resetting...' : 'Yes, reset'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

function getBillsComparator(orderBy, order) {
  return (a, b) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];
    if (orderBy === 'last_updated_at') {
      aVal = a.last_updated_at ?? a.created_at;
      bVal = b.last_updated_at ?? b.created_at;
    }
    if (orderBy === 'due') {
      aVal = Number(aVal) ?? 0;
      bVal = Number(bVal) ?? 0;
      const cmp = aVal - bVal;
      return order === 'asc' ? cmp : -cmp;
    }
    if (orderBy === 'created_at' || orderBy === 'last_updated_at') {
      aVal = new Date(aVal || 0).getTime();
      bVal = new Date(bVal || 0).getTime();
      const cmp = aVal - bVal;
      return order === 'asc' ? cmp : -cmp;
    }
    const aStr = String(aVal ?? '').toLowerCase();
    const bStr = String(bVal ?? '').toLowerCase();
    const cmp = aStr.localeCompare(bStr, undefined, { numeric: true });
    return order === 'asc' ? cmp : -cmp;
  };
}

function formatDateOnly(isoString) {
  if (!isoString) return '—';
  const d = new Date(isoString);
  return d.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

const isIncludeStatus = (row) => {
  const s = (row.status ?? '').toString().trim().toLowerCase();
  return s === 'include' || s === 'included';
};
const isExcludeStatus = (row) => {
  const s = (row.status ?? '').toString().trim().toLowerCase();
  return s === 'exclude' || s === 'excluded';
};

function BillsSubTab({ email = '', setAlert, setWarning }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [excludeLoading, setExcludeLoading] = useState(false);
  const [includeLoading, setIncludeLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [orderBy, setOrderBy] = useState('client_name');
  const [order, setOrder] = useState('asc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [excludeReason, setExcludeReason] = useState('');
  const [includeConfirmOpen, setIncludeConfirmOpen] = useState(false);
  const [includeConfirmRow, setIncludeConfirmRow] = useState(null);

  const fetchBills = async () => {
    try {
      const api = process.env.REACT_APP_GET_DUE_BILLS;
      if (!api) {
        setError('API not configured (REACT_APP_GET_DUE_BILLS)');
        setLoading(false);
        return;
      }
      const res = await axios.get(api);
      const data = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load due bills');
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBills();
  }, []);

  // Stable unique id per row (same row always same id across sort/page)
  const rowId = (row) => {
    const c = row.client_id != null ? String(row.client_id) : '';
    const r = row.reference_no != null ? String(row.reference_no) : '';
    const t = row.created_at != null ? String(row.created_at) : '';
    const id = `${c}|${r}|${t}`;
    return id || `${row.client_name ?? ''}-${t}`;
  };

  const searchLower = search.trim().toLowerCase();
  const filteredList = list.filter((row) => {
    // Status filter
    if (statusFilter === 'include' && !isIncludeStatus(row)) return false;
    if (statusFilter === 'exclude' && !isExcludeStatus(row)) return false;
    // Search filter
    if (!searchLower) return true;
    const client_name = (row.client_name ?? '').toString().toLowerCase();
    const reference_no = (row.reference_no ?? '').toString().toLowerCase();
    const client_id = (row.client_id ?? '').toString().toLowerCase();
    const billing_type = (row.billing_type ?? '').toString().toLowerCase();
    const due = (row.due ?? '').toString().toLowerCase();
    const created_at = (row.created_at ?? '').toString().toLowerCase();
    const excluded_reason = (row.excluded_reason ?? '').toString().toLowerCase();
    return (
      client_name.includes(searchLower) ||
      reference_no.includes(searchLower) ||
      client_id.includes(searchLower) ||
      billing_type.includes(searchLower) ||
      due.includes(searchLower) ||
      created_at.includes(searchLower) ||
      excluded_reason.includes(searchLower)
    );
  });

  const sortKey = orderBy === 'created_at' ? 'last_updated_at' : orderBy;
  const sortedList = [...filteredList].sort(getBillsComparator(sortKey, order));
  const totalRows = sortedList.length;
  const maxPage = totalRows <= 0 ? 0 : Math.max(0, Math.ceil(totalRows / rowsPerPage) - 1);
  const safePage = page > maxPage ? maxPage : page;
  const paginatedList = sortedList.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);
  const selectableRowsOnPage = paginatedList.filter((row) => !isExcludeStatus(row));
  const allSelectedOnPage = selectableRowsOnPage.length > 0 && selectableRowsOnPage.every((r) => selectedRows.has(rowId(r)));
  const someSelectedOnPage = selectableRowsOnPage.some((r) => selectedRows.has(rowId(r)));
  const someSelected = selectedRows.size > 0;

  const [excludeConfirmOpen, setExcludeConfirmOpen] = useState(false);

  const handleSelectAll = (e) => {
    e.stopPropagation();
    const checked = e.target.checked;
    const ids = selectableRowsOnPage.map((r) => rowId(r));
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (checked) {
        ids.forEach((id) => next.add(id));
      } else {
        ids.forEach((id) => next.delete(id));
      }
      return next;
    });
  };

  const handleSelectRow = (e, id) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleExcludeBillsFromRemindersClick = () => {
    setExcludeConfirmOpen(true);
  };

  const handleExportExcel = () => {
    const exportData = sortedList.map((row) => ({
      'Client Name': row.client_name ?? '-',
      'Reference No': row.reference_no ?? '-',
      'Client ID': row.client_id ?? '-',
      'Billing Type': row.billing_type ?? '-',
      'Due': row.due != null ? Number(row.due) : '-',
      'Last Updated At': formatDateTime(row.last_updated_at ?? row.created_at),
      'Status': row.status ?? '-',
      'Excluded Reason': row.excluded_reason ?? '-',
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Bills');
    XLSX.writeFile(wb, `Bills_${statusFilter}.xlsx`);
  };

  const handleExcludeConfirmClose = () => {
    setExcludeConfirmOpen(false);
    setExcludeReason('');
  };

  const handleExcludeConfirm = async () => {
    if (!excludeReason.trim()) return;
    const api = process.env.REACT_APP_POST_EXCLUDE_BILLS;
    if (!api) {
      setWarning?.('API not configured (REACT_APP_POST_EXCLUDE_BILLS)');
      return;
    }
    const reason = excludeReason.trim();
    const selectedList = list.filter((row) => selectedRows.has(rowId(row)));
    const data = selectedList.map((row) => ({
      client_name: row.client_name ?? '',
      reference_no: row.reference_no ?? '',
      reason,
      excluded_by: email || '',
    }));
    if (data.length === 0) {
      setExcludeConfirmOpen(false);
      setExcludeReason('');
      setSelectedRows(new Set());
      return;
    }
    const idsToRemove = new Set(selectedRows);
    setExcludeLoading(true);
    try {
      const res = await axios.post(api, { data });
      if (res?.status >= 200 && res?.status < 300) {
        setAlert?.('Bills excluded from reminders successfully.');
        setExcludeConfirmOpen(false);
        setExcludeReason('');
        setSelectedRows(new Set());
        await fetchBills();
      } else {
        setWarning?.(res?.data?.message || 'Failed to exclude bills from reminders.');
      }
    } catch (err) {
      setWarning?.(err?.response?.data?.message || err?.message || 'Failed to exclude bills from reminders.');
    } finally {
      setExcludeLoading(false);
    }
  };

  const handleIncludeClick = (row) => {
    setIncludeConfirmRow(row);
    setIncludeConfirmOpen(true);
  };

  const handleIncludeConfirmClose = () => {
    setIncludeConfirmOpen(false);
    setIncludeConfirmRow(null);
  };

  const handleIncludeConfirm = async () => {
    if (!includeConfirmRow) return;
    const api = process.env.REACT_APP_POST_INCLUDE_BILLS;
    if (!api) {
      setWarning?.('API not configured (REACT_APP_POST_INCLUDE_BILLS)');
      handleIncludeConfirmClose();
      return;
    }
    const payload = {
      client_name: includeConfirmRow.client_name ?? '',
      reference_no: includeConfirmRow.reference_no ?? '',
      included_by: email || '',
    };
    setIncludeLoading(true);
    try {
      const res = await axios.post(api, payload);
      if (res?.status >= 200 && res?.status < 300) {
        setAlert?.('Bill included in reminders successfully.');
        handleIncludeConfirmClose();
        await fetchBills();
      } else {
        setWarning?.(res?.data?.message || 'Failed to include bill in reminders.');
      }
    } catch (err) {
      setWarning?.(err?.response?.data?.message || err?.message || 'Failed to include bill in reminders.');
    } finally {
      setIncludeLoading(false);
    }
  };

  const handleSort = (columnId) => () => {
    const isAsc = orderBy === columnId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnId);
    setPage(0);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (totalRows > 0 && page > maxPage) setPage(maxPage);
  }, [totalRows, rowsPerPage, page, maxPage]);

  if (loading) {
    return (
      <div className="automated-reminders-loading">
        <CircularProgress size={36} />
      </div>
    );
  }
  if (error) {
    return <div className="automated-reminders-error">{error}</div>;
  }

  return (
    <div className="automated-reminders-tab-content">
      <div className="automated-reminders-toolbar">
        <TextField
          size="small"
          placeholder="Search client name, reference no, client ID, due, date..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 320 }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            sx={{ borderRadius: '6px', fontSize: '13px' }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="include">Include</MenuItem>
            <MenuItem value="exclude">Exclude</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleExportExcel}
          disabled={sortedList.length === 0}
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            borderColor: '#1e40af',
            color: '#1e40af',
            '&:hover': { borderColor: '#1d4ed8', backgroundColor: '#eef2ff' },
          }}
        >
          Export
        </Button>
        <Button
          variant="contained"
          disabled={!someSelected}
          onClick={handleExcludeBillsFromRemindersClick}
          sx={{
            textTransform: 'none',
            fontSize: '14px',
            backgroundColor: '#1e40af',
            '&:hover': { backgroundColor: '#1d4ed8' },
            '&:disabled': { backgroundColor: '#94a3b8', color: '#fff' },
          }}
        >
          Exclude bills from reminders
        </Button>
      </div>
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
          borderRadius: '8px',
          maxHeight: 'calc(100vh - 400px)',
          overflow: 'auto',
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 48, minWidth: 48, ...TABLE_HEADER_SX, padding: '10px 8px' }} onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  indeterminate={selectableRowsOnPage.length > 0 && someSelectedOnPage && !allSelectedOnPage}
                  checked={selectableRowsOnPage.length > 0 && allSelectedOnPage}
                  onChange={handleSelectAll}
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                  sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' } }}
                  inputProps={{ 'aria-label': 'Select all on page' }}
                />
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX }}>
                <TableSortLabel
                  active={orderBy === 'client_name'}
                  direction={orderBy === 'client_name' ? order : undefined}
                  onClick={handleSort('client_name')}
                  sx={{ color: '#fff' }}
                >
                  Client Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'reference_no'}
                  direction={orderBy === 'reference_no' ? order : undefined}
                  onClick={handleSort('reference_no')}
                  sx={{ color: '#fff' }}
                >
                  Reference No
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'client_id'}
                  direction={orderBy === 'client_id' ? order : undefined}
                  onClick={handleSort('client_id')}
                  sx={{ color: '#fff' }}
                >
                  Client ID
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'billing_type'}
                  direction={orderBy === 'billing_type' ? order : undefined}
                  onClick={handleSort('billing_type')}
                  sx={{ color: '#fff' }}
                >
                  Billing Type
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'due'}
                  direction={orderBy === 'due' ? order : undefined}
                  onClick={handleSort('due')}
                  sx={{ color: '#fff' }}
                >
                  Due
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'created_at'}
                  direction={orderBy === 'created_at' ? order : undefined}
                  onClick={handleSort('created_at')}
                  sx={{ color: '#fff' }}
                >
                  Last Updated At
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center', width: 80 }}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : undefined}
                  onClick={handleSort('status')}
                  sx={{ color: '#fff' }}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'left', minWidth: 180 }}>Excluded Reason</TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center', width: 80 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedList.length > 0 ? (
              paginatedList.map((row, idx) => {
                const id = rowId(row);
                const excluded = isExcludeStatus(row);
                const included = isIncludeStatus(row);
                const rowBg = excluded ? '#f0eaea' : idx % 2 === 0 ? '#ffffff' : '#f8faff';
                const rowHover = excluded ? '#e8e0e0' : '#e8f4fd';
                return (
                  <TableRow
                    key={id}
                    sx={{
                      '&:hover': { backgroundColor: rowHover },
                      backgroundColor: rowBg,
                    }}
                  >
                    <TableCell padding="checkbox" sx={{ padding: '8px', width: 48, minWidth: 48 }}>
                      {!excluded && (
                        <Checkbox
                          size="small"
                          checked={selectedRows.has(id)}
                          onChange={(e) => handleSelectRow(e, id)}
                          onClick={(e) => e.stopPropagation()}
                          inputProps={{ 'aria-label': 'Select bill' }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: '12px' }}>{row.client_name ?? '—'}</TableCell>
                    <TableCell sx={{ fontSize: '12px', textAlign: 'center' }}>{row.reference_no ?? '—'}</TableCell>
                    <TableCell sx={{ fontSize: '12px', textAlign: 'center' }}>{row.client_id ?? '—'}</TableCell>
                    <TableCell sx={{ fontSize: '12px', textAlign: 'center' }}>{row.billing_type ?? '—'}</TableCell>
                    <TableCell sx={{ fontSize: '12px', textAlign: 'center' }}>
                      {row.due != null ? `₹${Number(row.due).toLocaleString('en-IN')}` : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '12px', textAlign: 'center' }}>
                      {formatDateTime(row.last_updated_at ?? row.created_at)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '12px', textAlign: 'center' }}>{row.status ?? '—'}</TableCell>
                    <TableCell sx={{ fontSize: '12px', textAlign: 'left', maxWidth: 250, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {row.excluded_reason ?? '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '12px', textAlign: 'center', padding: '4px' }}>
                      {excluded && (
                        <IconButton
                          size="small"
                          onClick={() => handleIncludeClick(row)}
                          disabled={includeLoading}
                          aria-label="Include in reminders"
                          sx={{ color: '#1e40af', '&:hover': { backgroundColor: '#e8f4fd' } }}
                        >
                          <RestoreIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={10} sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
                  <Typography variant="body2">
                    {list.length === 0 ? 'No due bills found.' : 'No matching bills.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 15, 20, 50]}
        component="div"
        count={totalRows}
        rowsPerPage={rowsPerPage}
        page={safePage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ borderTop: '1px solid #e2e8f0', flexShrink: 0 }}
      />

      <Dialog open={excludeConfirmOpen} onClose={handleExcludeConfirmClose} maxWidth="sm" fullWidth>
        <DialogTitle>Exclude from reminders</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to exclude the selected bill(s) from reminders?
          </DialogContentText>
          <TextField
            fullWidth
            required
            label="Reason"
            placeholder="Enter reason for excluding (mandatory)"
            value={excludeReason}
            onChange={(e) => setExcludeReason(e.target.value)}
            multiline
            minRows={2}
            maxRows={4}
            size="small"
            error={excludeConfirmOpen && !excludeReason.trim()}
            helperText={excludeConfirmOpen && !excludeReason.trim() ? 'Reason is required' : ''}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleExcludeConfirmClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleExcludeConfirm}
            variant="contained"
            disabled={!excludeReason.trim() || excludeLoading}
            sx={{ backgroundColor: '#1e40af', '&:hover': { backgroundColor: '#1d4ed8' } }}
          >
            {excludeLoading ? 'Excluding...' : 'Yes, exclude'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={includeConfirmOpen} onClose={handleIncludeConfirmClose} maxWidth="sm" fullWidth>
        <DialogTitle>Include in reminders</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to include this bill in reminders?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleIncludeConfirmClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleIncludeConfirm}
            variant="contained"
            disabled={includeLoading}
            sx={{ backgroundColor: '#1e40af', '&:hover': { backgroundColor: '#1d4ed8' } }}
          >
            {includeLoading ? 'Including...' : 'Yes, include'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

function getRemindersHistoryComparator(orderBy, order) {
  return (a, b) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];
    if (orderBy === 'due') {
      aVal = Number(aVal) ?? 0;
      bVal = Number(bVal) ?? 0;
      const cmp = aVal - bVal;
      return order === 'asc' ? cmp : -cmp;
    }
    if (orderBy === 'reminder_sent_date' || orderBy === 'created_at') {
      aVal = new Date(aVal || 0).getTime();
      bVal = new Date(bVal || 0).getTime();
      const cmp = aVal - bVal;
      return order === 'asc' ? cmp : -cmp;
    }
    const aStr = String(aVal ?? '').toLowerCase();
    const bStr = String(bVal ?? '').toLowerCase();
    const cmp = aStr.localeCompare(bStr, undefined, { numeric: true });
    return order === 'asc' ? cmp : -cmp;
  };
}

function RemindersHistorySubTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('reminder_sent_date');
  const [order, setOrder] = useState('desc');

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const api = process.env.REACT_APP_GET_AUTOMATED_REMINDERS_HISTORY;
        if (!api) {
          setError('API not configured (REACT_APP_GET_AUTOMATED_REMINDERS_HISTORY)');
          setLoading(false);
          return;
        }
        const res = await axios.get(api);
        const data = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
        setList(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err?.message || 'Failed to load reminders history');
        setList([]);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  const searchLower = search.toLowerCase().trim();
  const filteredList = list.filter((row) => {
    if (!searchLower) return true;
    const client_name = (row.client_name ?? '').toLowerCase();
    const reminder_type = (row.reminder_type ?? '').toLowerCase();
    const subject_name = (row.subject_name ?? '').toLowerCase();
    const status = (row.status ?? '').toLowerCase();
    const mail_ids = (row.mail_ids ?? '').toLowerCase();
    const client_type = (row.client_type ?? '').toLowerCase();
    return (
      client_name.includes(searchLower) ||
      reminder_type.includes(searchLower) ||
      subject_name.includes(searchLower) ||
      status.includes(searchLower) ||
      mail_ids.includes(searchLower) ||
      client_type.includes(searchLower)
    );
  });

  const sortedList = [...filteredList].sort(getRemindersHistoryComparator(orderBy, order));
  const paginatedList = sortedList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= filteredList.length) setPage(0);
  }, [filteredList.length, page, rowsPerPage]);

  const handleSort = (columnId) => () => {
    const isAsc = orderBy === columnId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnId);
    setPage(0);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <div className="automated-reminders-loading">
        <CircularProgress size={36} />
      </div>
    );
  }
  if (error) {
    return <div className="automated-reminders-error">{error}</div>;
  }

  return (
    <div className="automated-reminders-tab-content">
      <div className="automated-reminders-toolbar">
        <TextField
          size="small"
          placeholder="Search reminder type, client, subject, status..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 280 }}
        />
      </div>
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
          borderRadius: '8px',
          maxHeight: 'calc(100vh - 380px)',
          overflow: 'auto',
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...TABLE_HEADER_SX }}>
                <TableSortLabel
                  active={orderBy === 'reminder_type'}
                  direction={orderBy === 'reminder_type' ? order : undefined}
                  onClick={handleSort('reminder_type')}
                  sx={{ color: '#fff' }}
                >
                  Reminder Type
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX }}>
                <TableSortLabel
                  active={orderBy === 'subject_name'}
                  direction={orderBy === 'subject_name' ? order : undefined}
                  onClick={handleSort('subject_name')}
                  sx={{ color: '#fff' }}
                >
                  Subject
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'client_name'}
                  direction={orderBy === 'client_name' ? order : undefined}
                  onClick={handleSort('client_name')}
                  sx={{ color: '#fff' }}
                >
                  Client Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'client_type'}
                  direction={orderBy === 'client_type' ? order : undefined}
                  onClick={handleSort('client_type')}
                  sx={{ color: '#fff' }}
                >
                  Client Type
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'due'}
                  direction={orderBy === 'due' ? order : undefined}
                  onClick={handleSort('due')}
                  sx={{ color: '#fff' }}
                >
                  Due
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : undefined}
                  onClick={handleSort('status')}
                  sx={{ color: '#fff' }}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'reminder_sent_date'}
                  direction={orderBy === 'reminder_sent_date' ? order : undefined}
                  onClick={handleSort('reminder_sent_date')}
                  sx={{ color: '#fff' }}
                >
                  Reminder Sent Date
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedList.length > 0 ? (
              paginatedList.map((row, idx) => (
                <TableRow
                  key={`${row.client_name}-${row.reminder_sent_date}-${idx}`}
                  sx={{
                    '&:hover': { backgroundColor: '#e8f4fd' },
                    backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8faff',
                  }}
                >
                  <TableCell sx={{ fontSize: '12px' }}>{row.reminder_type ?? '—'}</TableCell>
                  <TableCell sx={{ fontSize: '12px', maxWidth: 220 }} title={row.subject_name}>
                    {row.subject_name ?? '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '12px', textAlign: 'center' }}>{row.client_name ?? '—'}</TableCell>
                  <TableCell sx={{ fontSize: '12px', textAlign: 'center' }}>{row.client_type ?? '—'}</TableCell>
                  <TableCell sx={{ fontSize: '12px', textAlign: 'center' }}>
                    {row.due != null ? `₹${Number(row.due).toLocaleString('en-IN')}` : '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '12px', textAlign: 'center' }}>{row.status ?? '—'}</TableCell>
                  <TableCell sx={{ fontSize: '12px', textAlign: 'center' }}>
                    {formatDateTime(row.reminder_sent_date)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
                  <Typography variant="body2">
                    {list.length === 0 ? 'No reminders history found.' : 'No matching results.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 15, 20, 50]}
          component="div"
          count={sortedList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ borderTop: '1px solid #e2e8f0' }}
        />
      </TableContainer>
    </div>
  );
}

function getClientsComparator(orderBy, order) {
  return (a, b) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];
    if (orderBy === 'onboarded_at' || orderBy === 'updated_at') {
      aVal = new Date(aVal || 0).getTime();
      bVal = new Date(bVal || 0).getTime();
      const cmp = aVal - bVal;
      return order === 'asc' ? cmp : -cmp;
    }
    if (orderBy === 'saas_only' || orderBy === 'automation_restriction') {
      aVal = aVal ? 1 : 0;
      bVal = bVal ? 1 : 0;
      const cmp = aVal - bVal;
      return order === 'asc' ? cmp : -cmp;
    }
    const aStr = String(aVal ?? '').toLowerCase();
    const bStr = String(bVal ?? '').toLowerCase();
    const cmp = aStr.localeCompare(bStr, undefined, { numeric: true });
    return order === 'asc' ? cmp : -cmp;
  };
}

function ClientsRestrictionSubTab({ email = '', setAlert, setWarning }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('client_name');
  const [order, setOrder] = useState('asc');
  const [togglingIds, setTogglingIds] = useState(new Set());
  const [toggleConfirm, setToggleConfirm] = useState({ open: false, row: null, nextValue: false });
  const [toggleReason, setToggleReason] = useState('');
  const [toggleSaving, setToggleSaving] = useState(false);

  const fetchClients = async () => {
    try {
      const api = process.env.REACT_APP_GET_AUTOMATION_CLIENT_LIST;
      if (!api) {
        setError('API not configured (REACT_APP_GET_AUTOMATION_CLIENT_LIST)');
        setLoading(false);
        return;
      }
      const res = await axios.get(api);
      const data = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load clients');
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const searchLower = search.trim().toLowerCase();
  const filteredList = list.filter((row) => {
    if (!searchLower) return true;
    const client_id = (row.client_id ?? '').toString().toLowerCase();
    const client_name = (row.client_name ?? '').toString().toLowerCase();
    const group_name = (row.group_name ?? '').toString().toLowerCase();
    const billing_type = (row.billing_type ?? '').toString().toLowerCase();
    const status = (row.status ?? '').toString().toLowerCase();
    return (
      client_id.includes(searchLower) ||
      client_name.includes(searchLower) ||
      group_name.includes(searchLower) ||
      billing_type.includes(searchLower) ||
      status.includes(searchLower)
    );
  });

  const sortedList = [...filteredList].sort(getClientsComparator(orderBy, order));
  const totalRows = sortedList.length;
  const maxPage = totalRows <= 0 ? 0 : Math.max(0, Math.ceil(totalRows / rowsPerPage) - 1);
  const safePage = page > maxPage ? maxPage : page;
  const paginatedList = sortedList.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);

  useEffect(() => {
    if (totalRows > 0 && page > maxPage) setPage(maxPage);
  }, [totalRows, rowsPerPage, page, maxPage]);

  const handleSort = (columnId) => () => {
    const isAsc = orderBy === columnId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnId);
    setPage(0);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleToggleClick = (row) => {
    if (row?.client_id == null) return;
    setToggleReason('');
    setToggleConfirm({
      open: true,
      row,
      nextValue: !row.automation_restriction,
    });
  };

  const handleToggleClose = () => {
    if (toggleSaving) return;
    setToggleConfirm({ open: false, row: null, nextValue: false });
    setToggleReason('');
  };

  const handleToggleConfirm = async () => {
    const { row, nextValue } = toggleConfirm;
    if (!row) return;
    const reason = toggleReason.trim();
    if (!reason) return;
    const api = process.env.REACT_APP_UPDATE_DUE_DATA;
    if (!api) {
      setWarning?.('API not configured (REACT_APP_UPDATE_DUE_DATA)');
      return;
    }
    const id = row.client_id;
    setToggleSaving(true);
    setTogglingIds((prev) => new Set(prev).add(id));
    try {
      const payload = {
        client_id: id,
        status: nextValue ? 1 : 0,
        reason,
        updated_by: email || '',
      };
      const res = await axios.post(api, payload);
      if (res?.status >= 200 && res?.status < 300) {
        setAlert?.(nextValue ? 'Automation restriction enabled.' : 'Automation restriction disabled.');
        setToggleConfirm({ open: false, row: null, nextValue: false });
        setToggleReason('');
        await fetchClients();
      } else {
        setWarning?.(res?.data?.message || 'Failed to update restriction.');
      }
    } catch (err) {
      setWarning?.(err?.response?.data?.message || err?.message || 'Failed to update restriction.');
    } finally {
      setToggleSaving(false);
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="automated-reminders-loading">
        <CircularProgress size={36} />
      </div>
    );
  }
  if (error) {
    return <div className="automated-reminders-error">{error}</div>;
  }

  return (
    <div className="automated-reminders-tab-content">
      <div className="automated-reminders-toolbar">
        <TextField
          size="small"
          placeholder="Search client ID, name, group, billing type, status..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 320 }}
        />
      </div>

      <TableContainer
        component={Paper}
        sx={{
          boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
          borderRadius: '8px',
          maxHeight: 'calc(100vh - 400px)',
          overflow: 'auto',
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...TABLE_HEADER_SX }}>
                <TableSortLabel
                  active={orderBy === 'client_id'}
                  direction={orderBy === 'client_id' ? order : undefined}
                  onClick={handleSort('client_id')}
                  sx={{ color: '#fff' }}
                >
                  Client ID
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'client_name'}
                  direction={orderBy === 'client_name' ? order : undefined}
                  onClick={handleSort('client_name')}
                  sx={{ color: '#fff' }}
                >
                  Client Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'group_name'}
                  direction={orderBy === 'group_name' ? order : undefined}
                  onClick={handleSort('group_name')}
                  sx={{ color: '#fff' }}
                >
                  Group Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'billing_type'}
                  direction={orderBy === 'billing_type' ? order : undefined}
                  onClick={handleSort('billing_type')}
                  sx={{ color: '#fff' }}
                >
                  Billing Type
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'saas_only'}
                  direction={orderBy === 'saas_only' ? order : undefined}
                  onClick={handleSort('saas_only')}
                  sx={{ color: '#fff' }}
                >
                  Saas Only
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'onboarded_at'}
                  direction={orderBy === 'onboarded_at' ? order : undefined}
                  onClick={handleSort('onboarded_at')}
                  sx={{ color: '#fff' }}
                >
                  Onboarded At
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : undefined}
                  onClick={handleSort('status')}
                  sx={{ color: '#fff' }}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'updated_by'}
                  direction={orderBy === 'updated_by' ? order : undefined}
                  onClick={handleSort('updated_by')}
                  sx={{ color: '#fff' }}
                >
                  Updated By
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'updated_at'}
                  direction={orderBy === 'updated_at' ? order : undefined}
                  onClick={handleSort('updated_at')}
                  sx={{ color: '#fff' }}
                >
                  Updated At
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'left', minWidth: 180 }}>
                Reason
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_SX, textAlign: 'center', width: 100 }}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedList.length > 0 ? (
              paginatedList.map((row, idx) => {
                const id = row.client_id ?? `idx-${idx}`;
                const isToggling = togglingIds.has(row.client_id);
                return (
                  <TableRow
                    key={`${id}-${idx}`}
                    sx={{
                      '&:hover': { backgroundColor: '#e8f4fd' },
                      backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8faff',
                    }}
                  >
                    <TableCell sx={{ fontSize: '12px' }}>{row.client_id ?? '—'}</TableCell>
                    <TableCell sx={{ fontSize: '12px', textAlign: 'center' }}>{row.client_name ?? '—'}</TableCell>
                    <TableCell sx={{ fontSize: '12px', textAlign: 'center' }}>{row.group_name ?? '-'}</TableCell>
                    <TableCell sx={{ fontSize: '12px', textAlign: 'center' }}>{row.billing_type ?? '—'}</TableCell>
                    <TableCell sx={{ fontSize: '12px', textAlign: 'center' }}>{row.saas_only ? 'Yes' : 'No'}</TableCell>
                    <TableCell sx={{ fontSize: '12px', textAlign: 'center' }}>{formatDateOnly(row.onboarded_at)}</TableCell>
                    <TableCell sx={{ fontSize: '12px', textAlign: 'center' }}>{row.status ?? '—'}</TableCell>
                    <TableCell sx={{ fontSize: '12px', textAlign: 'center' }}>{row.updated_by ?? '—'}</TableCell>
                    <TableCell sx={{ fontSize: '12px', textAlign: 'center' }}>{formatDateTime(row.updated_at)}</TableCell>
                    <TableCell sx={{ fontSize: '12px', textAlign: 'left', maxWidth: 250, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {row.reason ?? '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '12px', textAlign: 'center', padding: '4px' }}>
                      <Switch
                        size="small"
                        checked={!!row.automation_restriction}
                        disabled={isToggling}
                        onChange={() => handleToggleClick(row)}
                        inputProps={{ 'aria-label': 'Toggle automation restriction' }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={11} sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
                  <Typography variant="body2">
                    {list.length === 0 ? 'No clients found.' : 'No matching clients.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 15, 25, 50]}
        component="div"
        count={totalRows}
        rowsPerPage={rowsPerPage}
        page={safePage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ borderTop: '1px solid #e2e8f0' }}
      />

      <Dialog
        open={toggleConfirm.open}
        onClose={handleToggleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {toggleConfirm.nextValue ? 'Enable' : 'Disable'} automation restriction
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to{' '}
            <strong>{toggleConfirm.nextValue ? 'enable' : 'disable'}</strong>{' '}
            automation restriction for{' '}
            <strong>{toggleConfirm.row?.client_name ?? toggleConfirm.row?.client_id ?? '—'}</strong>?
          </DialogContentText>
          <TextField
            fullWidth
            required
            autoFocus
            label="Reason"
            placeholder="Enter reason (mandatory)"
            value={toggleReason}
            onChange={(e) => setToggleReason(e.target.value)}
            multiline
            minRows={2}
            maxRows={4}
            size="small"
            error={toggleConfirm.open && !toggleReason.trim()}
            helperText={
              toggleConfirm.open && !toggleReason.trim()
                ? 'Reason is required'
                : ''
            }
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleToggleClose} color="inherit" disabled={toggleSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleToggleConfirm}
            variant="contained"
            disabled={!toggleReason.trim() || toggleSaving}
            sx={{ backgroundColor: '#1e40af', '&:hover': { backgroundColor: '#1d4ed8' } }}
          >
            {toggleSaving
              ? 'Saving...'
              : toggleConfirm.nextValue
                ? 'Yes, enable'
                : 'Yes, disable'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

const SUB_TABS = [
  { id: 'reminder', label: 'REMINDER' },
  { id: 'bills', label: 'BILLS' },
  { id: 'reminders-history', label: 'REMINDERS HISTORY' },
  { id: 'clients-restriction', label: 'CLIENTS RESTRICTION' },
];

function AutomatedReminders({ email = '', setAlert, setWarning }) {
  const [activeTab, setActiveTab] = useState('reminder');

  return (
    <div className="automated-reminders">
      <div className="automated-reminders-sub-tabs">
        {SUB_TABS.map((tab) => (
          <div
            key={tab.id}
            className={`automated-reminders-tab-item ${activeTab === tab.id ? 'automated-reminders-tab-active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </div>
        ))}
      </div>

      <div className="automated-reminders-panel">
        {activeTab === 'reminder' && <ReminderSubTab email={email} setAlert={setAlert} setWarning={setWarning} />}
        {activeTab === 'bills' && <BillsSubTab email={email} setAlert={setAlert} setWarning={setWarning} />}
        {activeTab === 'reminders-history' && <RemindersHistorySubTab />}
        {activeTab === 'clients-restriction' && <ClientsRestrictionSubTab email={email} setAlert={setAlert} setWarning={setWarning} />}
      </div>
    </div>
  );
}

export default AutomatedReminders;
